import { Autocomplete, Box, Grid } from "@mui/material";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { useCallback, useEffect, useState } from "react";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import Loader from "../../../components/common/loader/Loader";
import getAxiosError from "../../../app/api/axiosError";
import { fetchStoreRateCardAssociationList } from "../../../app/features/ratecard/ratecardSlice";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { ParcelCenterRateCardAssociationResponse } from "../ratecard/dtos/StoreRatecardAssociationResponse";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import { ClientInvoiceManualRequest } from "../../../app/models/requestModels/ClientInvoiceRequests";
import { ClientInvoiceAgent } from "../../../app/api/clientInvoices/ClientInvoiceAgent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { formatDate } from "../../../components/common/utils/DateUtils";

// type to recieve parameter for this component to initialize
interface ManualGenerateClientInvoiceModalProps {
  clientId: number;
  clientType: string;
  closeManualGenerateClientInvoiceModal: CallableFunction;
  onCompleted: CallableFunction;
}

interface ManualGenerateClientInvoiceUiRequest {
  startDate: Dayjs;
  endDate: Dayjs;
  storeIds: number[];
}

// manually generate a client invoice
const ManualGenerateClientInvoiceModal = (
  props: ManualGenerateClientInvoiceModalProps
) => {
  const initialValues: ManualGenerateClientInvoiceUiRequest = {
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
    storeIds: [],
  };

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchStoreRateCardAssociationData = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchStoreRateCardAssociationList(props.clientId)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "storeRateCardAssociationList",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "storeRateCardAssociationException",
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    setIsLoading(true);
    let isFunctionRun = true;
    if (isFunctionRun) {
      fetchStoreRateCardAssociationData();
    }

    return () => {
      isFunctionRun = false;
    };
  }, [fetchStoreRateCardAssociationData]);

  // import store data from "Redux-Store"
  const { isLoading: isStoresLoading, storeRateCardAssociationList } =
    useSelector((state: RootState) => state.ratecard);

  const parcelCentersRateCardAssociationList: ParcelCenterRateCardAssociationResponse[] =
    []; // TODO: to be implemented for parcel centers

  // validation rule while adding new ratecard into the database
  const validationSchema = Yup.object().shape({
    startDate: Yup.date().required(),
    endDate: Yup.date()
      .required("Please select the end date")
      .min(Yup.ref("startDate"), "End date must be after start date"),
    storeIds: Yup.array().min(1, "Pick at least one store").of(Yup.number()),
  });

  const mapUiRequestToServerRequest = (
    uiRequest: ManualGenerateClientInvoiceUiRequest
  ): ClientInvoiceManualRequest => {
    let serverRequest: ClientInvoiceManualRequest = {
      clientId: props.clientId,
      startDate: formatDate(uiRequest.startDate.toDate()),
      endDate: formatDate(uiRequest.endDate.toDate()),
      storeIds: uiRequest.storeIds.join(","),
    };
    return serverRequest;
  };

  //on form submit make server call to manually generate the client invoice
  const handleSubmit = async (
    uiRequest: ManualGenerateClientInvoiceUiRequest
  ) => {
    const serverRequest = mapUiRequestToServerRequest(uiRequest);

    try {
      const response = await ClientInvoiceAgent.manuallyGenerateClientInvoice(
        serverRequest
      );
      if (response.status !== 200) {
        toast.error("Couldn't manually generate the client invoice", {
          toastId: "manuallyGenerateClientInvoice",
        });
      }

      const fileName = response.headers["file-name"];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      const appError = getAxiosError(error);
      if (appError.code === "400")
        toast.error(
          "Couldn't manually generate the client invoice - No Data - BadRequest",
          {
            toastId: "manuallyGenerateClientInvoiceException",
          }
        );
      else
        toast.error(appError.errorMessage, {
          toastId: "manuallyGenerateClientInvoiceException",
        });
    } finally {
      setIsLoading(false);
      props.onCompleted();
    }
    return true;
  };

  const storeAndParcelCenterAssociationList = storeRateCardAssociationList.map(
    (storeAssociation) => {
      return {
        ...storeAssociation,
        group: "Stores",
        isStore: true,
        nameIdentifier: storeAssociation.storeId, // adding name Identifier as stores and parcel centers don't have a common attribute for the name
      };
    }
  );

  return (
    <>
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(
          values: ManualGenerateClientInvoiceUiRequest,
          { setSubmitting }
        ) => {
          setSubmitting(true);
          setIsLoading(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          errors,
          isValid,
        }) => {
          return (
            <SubmitModalWithFormik
              title="Manually generate client invoices"
              closeSubmitModal={props.closeManualGenerateClientInvoiceModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="xs"
              isLoading={false}
              submitButtonName="Generate"
              closeModalAfterSubmit={false}
              fullWidth={false}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Loader
                  isLoading={isStoresLoading || isSubmitting || isLoading}
                />
                <Form>
                  <Grid container>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          name="startDate"
                          label="Select start date"
                          value={values.startDate}
                          disableFuture
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              margin: "normal",
                            },
                          }}
                          views={["year", "month", "day"]}
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setFieldValue(`startDate`, newValue);
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          name="endDate"
                          label="Select end date"
                          value={values.endDate}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              margin: "normal",
                              error: !isValid && errors.endDate !== undefined,
                              helperText: isValid
                                ? ""
                                : `${errors.endDate ?? ""}`,
                            },
                          }}
                          disableFuture
                          views={["year", "month", "day"]}
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setFieldValue(`endDate`, newValue);
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Autocomplete
                        multiple
                        id="storeIds"
                        options={storeAndParcelCenterAssociationList}
                        groupBy={(option) => option.group}
                        limitTags={3}
                        disableCloseOnSelect
                        size="small"
                        onChange={(event, value) =>
                          setFieldValue(
                            "storeIds",
                            value.map((x) => x.id)
                          )
                        }
                        value={storeAndParcelCenterAssociationList.filter((x) =>
                          values.storeIds.includes(x.id)
                        )}
                        getOptionDisabled={(option) => !option.isAssociated}
                        getOptionLabel={(option) =>
                          `${option.nameIdentifier} ${
                            !option.isAssociated
                              ? ` - No ratecard association`
                              : ``
                          }`
                        }
                        sx={{
                          // overring font-family else it chose different font-family on its own!
                          fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                        }}
                        renderInput={(params) => (
                          <FormikTextField
                            {...params}
                            margin="normal"
                            name="storeIds"
                            label={
                              props.clientType === "both"
                                ? "Select Parcel Centers/Stores"
                                : props.clientType === "e2e"
                                ? "Select Parcel Centers"
                                : "Select Stores"
                            }
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </Box>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
    </>
  );
};

export default ManualGenerateClientInvoiceModal;
